/**
 * Channel Partner Hub shell — Forest band (title, CP code, Hub bell), tab
 * navigation, then the active view.
 *
 * `/me.can_access_hub` is the only gate: without it (never approved, or an
 * older profile without a current approval) the Hub bounces to the status
 * page, which explains the account's state.
 */
import { Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { ClipboardList, LayoutGrid, UserRound, Users } from 'lucide-react';
import { AnimatedTabs, Button, ErrorMessage, Spinner } from '@finmin/ui';
import { useChannelPartnerContext } from '@/api/channel-partners';
import { ChannelPartnerHero } from './cp-hero';
import { HubNotificationBell } from './hub-notification-bell';

const HUB_TABS = [
  { value: 'overview', label: 'Overview', icon: LayoutGrid, to: '/channel-partner/hub' },
  { value: 'customers', label: 'Customers', icon: Users, to: '/channel-partner/hub/customers' },
  { value: 'services', label: 'Services', icon: ClipboardList, to: '/channel-partner/hub/services' },
  { value: 'profile', label: 'Profile', icon: UserRound, to: '/channel-partner/hub/profile' },
] as const;

type HubTab = (typeof HUB_TABS)[number]['value'];

function activeTab(pathname: string): HubTab {
  const path = pathname.replace(/\/+$/, '');
  const match = HUB_TABS.find((t) => t.value !== 'overview' && path.startsWith(t.to));
  return match?.value ?? 'overview';
}

export function ChannelPartnerHubLayout() {
  const me = useChannelPartnerContext();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (me.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (me.error || !me.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-10 sm:px-6">
        <ErrorMessage message={me.error?.message ?? 'Could not load your Channel Partner details.'} />
        <Button variant="outline" onClick={() => void me.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!me.data.can_access_hub) return <Navigate to="/channel-partner" replace />;

  const code = me.data.profile?.cp_code ?? null;
  const firstName = me.data.account.name?.trim().split(/\s+/)[0];

  return (
    <div className="min-h-screen bg-background">
      <ChannelPartnerHero
        eyebrow="FinminOS Channel Partner"
        title="Channel Partner Hub"
        description={
          <>
            Thank you for being our channel partner{firstName ? `, ${firstName}` : ''}.{' '}
            <span className="font-semibold text-cream">Here is your summary.</span> Every customer
            you&apos;ve brought in has helped FinminOS grow — we&apos;re grateful for the trust
            you&apos;ve placed in us.
          </>
        }
      >
        {code && (
          <div className="rounded-md border border-cream/25 px-3 py-1.5 text-right">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-cream/70">
              Your partner code
            </p>
            <p className="font-mono text-base font-bold tracking-widest text-cream">{code}</p>
          </div>
        )}
        <HubNotificationBell />
      </ChannelPartnerHero>

      <div className="sticky top-16 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <AnimatedTabs
            items={HUB_TABS}
            value={activeTab(pathname)}
            onChange={(value) => {
              const tab = HUB_TABS.find((t) => t.value === value);
              if (tab) void navigate({ to: tab.to });
            }}
            accent="amber"
            ariaLabel="Channel Partner Hub sections"
          />
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
